import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { getReports, clearReportsCache } from '../services/reportService';
import { useSession } from '../context/SessionContext';
import LoadingIndicator from '../components/LoadingIndicator';
import '../styles/ReportsListPage.css';
import axios from '../services/axiosConfig'; // Import axios for API calls
import { callMainAPI, callMainAPIAndLoadReports, authService, checkAuthenticationStatus } from '../services/authService';

const API_URL = process.env.REACT_APP_API_URL;

const ReportsListPage = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [reports, setReports] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showKeywordTags, setShowKeywordTags] = useState(true);
  const [isMockData, setIsMockData] = useState(false);
  const [apiResponseStructure, setApiResponseStructure] = useState(null);
  const [currentMenuId, setCurrentMenuId] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get session data from context
  // memberId/cid/caseId/systemId/userId are resolved from sessionData → user → samlRequestParams
  const { sessionData, isLoading: sessionLoading, error: sessionError, sessionParams,
          userId: ctxUserId, memberId: ctxMemberId, cid: ctxCid, caseId: ctxCaseId, systemId: ctxSystemId } = useSession();
  
  // Create a ref for the grid container to control scrolling
  const gridContainerRef = useRef(null);
  
  // Get menuId from URL query parameters if available
  const getMenuIdFromUrl = () => {
    const queryParams = new URLSearchParams(location.search);
    const menuId = queryParams.get('menuId');
    // If menuId is null/undefined/empty, use 0 as default value
    const parsedMenuId = menuId ? parseInt(menuId, 10) : 0;
    console.log('🔍 Getting menuId from URL:', { rawMenuId: menuId, parsedMenuId, url: location.search });
    return parsedMenuId;
  };
  
  // Function to reset scroll position to top
  const scrollToTop = () => {
    if (gridContainerRef.current) {
      gridContainerRef.current.scrollTop = 0;
    }
  };
  
  // Set initial menuId when component mounts
  useEffect(() => {
    const menuId = getMenuIdFromUrl();
    setCurrentMenuId(menuId);
    
    // No longer calling check-auth-status API directly - handled by authService
  }, []);

  // Function to check authentication status via centralized auth service
  const verifyAuthentication = async () => {
    try {
      // Use the centralized auth service instead of direct API call
      const authResult = await checkAuthenticationStatus();
      
      if (!authResult.isAuthenticated) {
        navigate('/', { replace: true });
        return false;
      }
      
      return true;
      
    } catch (error) {
      navigate('/', { replace: true });
      return false;
    }
  };

  // Flag to track if we've already fetched reports in this session
  const [reportsFetched, setReportsFetched] = useState(false);

  // Define fetchReports function outside useEffect
  const fetchReports = async () => {
    // First verify authentication before making any API calls
    const isAuthenticated = await verifyAuthentication();
    if (!isAuthenticated) {
      return;
    }

    // Resolve userId from context (falls back to samlRequestParams via SessionContext)
    const resolvedUserId = sessionData?.userId || sessionData?.UserId || ctxUserId;
    if (!resolvedUserId) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const userId = resolvedUserId;
      // Get menuId from URL
      const menuId = getMenuIdFromUrl();
      
      console.log('📊 fetchReports running with menuId:', menuId);
      
      // Get previous menuId to check if it changed
      const prevMenuId = currentMenuId;
      if (prevMenuId !== menuId) {
        console.log(`⚠️ MenuId changed from ${prevMenuId} to ${menuId} - forcing fresh API call`);
        
        // Force a cache reset for ReportList API in authService
        if (authService.reportListData) {
          const menuIdKey = menuId !== undefined && menuId !== null ? menuId.toString() : '0';
          // Delete this specific menuId's cached data
          delete authService.reportListData[menuIdKey];
          console.log('🔄 Removed cached data for menuId:', menuIdKey);
        }
      }
      
      // Don't set adminUser here - we'll let the authService handle it from the Main API response
      // Create unique cache key for this page load using just userId and menuId
      // We'll look for any cache key that starts with this prefix, since authService will include adminUser in the key
      const cacheKeyPrefix = `${userId}-`;
      
      // Always clear cache for the current menuId to ensure fresh data when tabs are clicked
      if (menuId !== null) {
        console.log('🧹 Clearing cache in fetchReports for menuId:', menuId);
        clearReportsCache(menuId);
      }
      
      // Update the current menuId
      setCurrentMenuId(menuId);
      
      // Make the API call with the current menuId
      console.log('📞 Making API call to fetch reports with menuId:', menuId);
      
      // Set forceRefresh=true to bypass caching in authService
      const forceRefresh = true;
      
      // Parameters for the Main API — use directly-exposed context values which fall
      // back through sessionData → user → samlRequestParams (see SessionContext).
      const mainApiParams = {
        userID:   ctxUserId   || sessionData?.userId   || '',
        memberId: ctxMemberId || sessionData?.memberId || '',
        cid:      ctxCid      || sessionData?.cid      || '',
        caseId:   ctxCaseId   || sessionData?.caseId   || '',
        systemId: ctxSystemId || sessionData?.systemId || sessionData?.SystemId || ''
      };
      
      // Use the callMainAPIAndLoadReports function which properly chains the calls
      // Pass menuId separately for the reports API call, and set forceRefresh=true
      const mainApiResponse = await callMainAPIAndLoadReports(mainApiParams, menuId, forceRefresh);
      
      if (!mainApiResponse) {
        throw new Error('Failed to initialize session with Main API');
      }
      
      // Get the reports data from the callMainAPIAndLoadReports return value
      let result = mainApiResponse.reportsData || {};
      
      setIsMockData(!!result?.isMockData);
      
      if (result && result.reports) {
        const firstReport = Array.isArray(result.reports) && result.reports.length > 0 ? result.reports[0] : null;
        if (firstReport) {
          setApiResponseStructure(Object.keys(firstReport).join(', '));
        }
      }
      
      setReports(result?.reports || []);
      setShowKeywordTags(!!result.showKeywordTags || true);
      setReportsFetched(true);  // Mark that we've fetched reports
      
      // No need to cache here - authService already handles caching with the proper adminUser value
      
      setLoading(false);
      scrollToTop();
    } catch (err) {
      console.error('Error in fetchReports:', err);
      setError(err.message || 'Failed to load reports. Please try again later.');
      setLoading(false);
    }
  };
  
  // Fetch reports when component mounts or when menuId changes in URL
  useEffect(() => {
    const menuId = getMenuIdFromUrl();
    if (!sessionData?.userId) return;

    // Always fetch new reports when the URL search params change (tab click)
    console.log('🔄 URL search params or sessionData.userId changed. Current menuId:', currentMenuId, 'New menuId:', menuId);

    // Check if menuId has changed
    if (currentMenuId !== menuId) {
      console.log(`🔀 MenuId changed from ${currentMenuId} to ${menuId} in URL effect`);
      // Force a fresh API call by modifying the reportListData in authService
      if (authService && authService.reportListData) {
        const menuIdKey = menuId !== undefined && menuId !== null ? menuId.toString() : '0';
        delete authService.reportListData[menuIdKey];
        console.log('🧨 Forcing fresh API call by removing cached data for menuId:', menuIdKey);
      }
      // Clear cache for the new menuId to ensure we get fresh data
      if (menuId !== null) {
        console.log('🗑️ Clearing cache for menuId:', menuId);
        clearReportsCache(menuId);
      }
      // Update current menuId state
      setCurrentMenuId(menuId);
    }
    // Reset the reportsFetched flag to force a new API call
    setReportsFetched(false);
    console.log('🚀 Calling fetchReports() from URL/sessionData effect with menuId:', menuId);
    fetchReports();
  }, [location.search, sessionData?.userId]); // Depend on both location.search and sessionData.userId
  
  // Reset scroll position when route changes (tab switching)
  useEffect(() => {
    scrollToTop();
  }, [location.pathname, location.search]);
  
  // Reset scroll when search term change
  useEffect(() => {
    scrollToTop();
  }, [searchTerm]);
  
  // Helper functions to extract report properties with correct names
  // NOTE: These must be declared BEFORE handleReportClick and filteredReports which call them.
  const getReportId = (report) => {
    if (report && typeof report === 'object') {
      return report.rptid || report.rptId || report.ReportId || report.reportId || report.Id || report.id || null;
    }
    return null;
  };

  const getReportName = (report) => {
    return report.rptname || report.rptName || report.ReportName || report.reportName || report.Name || report.name || '';
  };

  const getReportDescription = (report) => {
    return report.RptDesr || report.rptDesr || report.RptDesc || report.rptdesr || report.rptDesc || report.ReportDescription || report.reportDescription || report.Description || report.description || '';
  };

  const getReportKeywords = (report) => {
    return report.keywordsandtags ||report.KeywordsAndTags|| report.keywordsTags || report.Keywords || report.keywords || '';
  };

  // Function to handle report click with required API post format
  const handleReportClick = async (report) => {
    try {
      setLoading(true);
      const reportId = getReportId(report);
      const reportName = getReportName(report);
      const reportDescription = getReportDescription(report);
      const rptname = report.rptname || '';
      const rptdesr = report.rptdesr || '';
      // Check if reportId is null or undefined and handle it
      if (reportId === null || reportId === undefined) {
        console.error('Report ID is null or undefined:', report);
        setError('Unable to view this report: Report ID is missing');
        setLoading(false);
        return;
      }
      
      // Navigate to the report view page with menuId as query parameter
      // Make sure to pass the current menuId from the page where the report was listed
      navigate(`/view-report-2014/${reportId}?menuId=${currentMenuId}`, {
        state: {
          reportName,
          reportDescription,
          rptname,
          rptdesr
        }
      });
    } catch (err) {
      console.error('Error handling report click:', err);
      setError(err.message || 'Failed to load report details. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // Filter reports based on search term
  const filteredReports = searchTerm.trim() !== '' 
    ? reports.filter(report => {
        const searchLower = searchTerm.toLowerCase();
        const name = getReportName(report).toLowerCase();
        const description = getReportDescription(report).toLowerCase();
        const keywords = getReportKeywords(report).toLowerCase();
        return (
          name.includes(searchLower) || 
          description.includes(searchLower) ||
          (showKeywordTags && keywords.includes(searchLower))
        );
      })
    : reports;
  
  return (
    <div className="reports-list-page">
      {loading && <LoadingIndicator message="Loading reports..." />}
      
      <div className="content-container">
        {isMockData && (
          <div className="mock-data-warning">
            Note: Displaying mock data because the API server is not available.
          </div>
        )}
        
        <div className="search-container">
          <label htmlFor="search-input">Search Report:</label>
          <input 
            id="search-input" 
            type="text" 
            placeholder="Search by Report Name" 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        
        {error && <div className="error-message">{error}</div>}
        
        <div className="grid-container" ref={gridContainerRef}>
          <table className="reports-table">
            <thead>
              <tr>
                <th className="report-name-col">Report Name</th>
                <th className="report-desc-col">Report Description</th>
                {showKeywordTags && <th className="report-keywords-col">Keywords & Tags</th>}
              </tr>
            </thead>
            <tbody>
              {filteredReports.length === 0 ? (
                <tr>
                  <td colSpan={showKeywordTags ? 3 : 2} className="no-reports-message">
                    {loading ? 'Loading reports...' : error ? 'Error loading reports' : 'No reports available'}
                  </td>
                </tr>
              ) : (
                filteredReports.map((report, index) => {
                  // Extract report properties using helper functions
                  const reportId = getReportId(report);
                  const reportName = getReportName(report);
                  const reportDescription = getReportDescription(report);
                  const keywordTags = getReportKeywords(report);
                  
                  return (
                    <tr key={reportId || index} className={index % 2 === 0 ? 'even-row' : 'odd-row'}>
                      <td className="report-name-cell">
                        {/* Replace Link with button/div that calls handleReportClick */}
                        <div 
                          onClick={() => handleReportClick(report)} 
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              handleReportClick(report);
                            }
                          }}
                          tabIndex="0"
                          role="button"
                          aria-label={`Open report: ${reportName}`}
                          className="report-link"
                          style={{ cursor: 'pointer' }}
                        >
                          {reportName}
                        </div>
                      </td>
                      <td className="report-desc-cell">{reportDescription}</td>
                      {showKeywordTags && <td className="report-keywords-cell">{keywordTags}</td>}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ReportsListPage;
